import { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { HotelGeoData } from '../../services/investigationApi';
import { MAP_TILE_URL, SATELLITE_TILE_URL, type ViewMode } from './mapConstants';

// Fix default marker icons broken by webpack
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

export { MAP_TILE_URL, SATELLITE_TILE_URL } from './mapConstants';
export type { ViewMode } from './mapConstants';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface HotelGeoMapProps {
  hotels: HotelGeoData[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onHotelClick?: (hotel: HotelGeoData) => void;
}

/**
 * Hotel geolocation map component using Leaflet.
 * Supports map/satellite toggle and multiple hotel markers.
 */
export default function HotelGeoMap({ hotels, center, zoom = 13, onHotelClick }: HotelGeoMapProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('map');

  const mapCenter = center ?? (() => {
    const valid = hotels.filter((h) => h.latitude != null && h.longitude != null);
    if (valid.length === 0) return { lat: -4.3217, lng: 15.3125 }; // Kinshasa par défaut
    const avgLat = valid.reduce((s, h) => s + (h.latitude ?? 0), 0) / valid.length;
    const avgLng = valid.reduce((s, h) => s + (h.longitude ?? 0), 0) / valid.length;
    return { lat: avgLat, lng: avgLng };
  })();

  const tileUrl = viewMode === 'satellite' ? SATELLITE_TILE_URL : MAP_TILE_URL;

  return (
    <div className="relative w-full h-full min-h-[400px] rounded-lg overflow-hidden border border-gray-200">
      {/* Toggle carte / satellite */}
      <div className="absolute top-3 right-3 z-[1000] flex rounded-lg overflow-hidden shadow bg-white">
        <button
          onClick={() => setViewMode('map')}
          className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'map' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          aria-label="Vue carte"
        >
          Carte
        </button>
        <button
          onClick={() => setViewMode('satellite')}
          className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'satellite' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          aria-label="Vue satellite"
        >
          Satellite
        </button>
      </div>

      <MapContainer
        center={[mapCenter.lat, mapCenter.lng]}
        zoom={zoom}
        className="w-full h-full"
        style={{ minHeight: '400px' }}
      >
        <TileLayer url={tileUrl} attribution="&copy; OpenStreetMap contributors" />
        {hotels
          .filter((h) => h.latitude != null && h.longitude != null)
          .map((hotel) => (
            <Marker
              key={hotel.hotel_id}
              position={[hotel.latitude!, hotel.longitude!]}
              eventHandlers={{ click: () => onHotelClick?.(hotel) }}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">{hotel.name}</p>
                  <p>{hotel.full_address}</p>
                  <p className="text-gray-500">
                    {hotel.province} / {hotel.commune}
                  </p>
                  {hotel.altitude != null && (
                    <p className="text-xs text-gray-400">Altitude : {hotel.altitude}m</p>
                  )}
                  {hotel.access_notes && (
                    <p className="text-xs text-gray-400">{hotel.access_notes}</p>
                  )}
                  {hotel.nearby_landmarks && (
                    <p className="text-xs text-gray-400">Repères : {hotel.nearby_landmarks}</p>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
      </MapContainer>
    </div>
  );
}
